import React, { useMemo, useRef, useState } from 'react';
import { FileUp, Upload, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { REDIS_API_URL } from '@/services/api/config';
import { CreateLectureData } from '@/types';
import { getEventStyles } from '@/utils/operational-ui.utils';

const MAX_LECTURES = 1000;
const PREVIEW_LIMIT = 20;

interface ParsedImportData {
  deviceId?: string;
  lectures: CreateLectureData[];
}

const parseJsonToLectures = (value: unknown): ParsedImportData => {
  if (Array.isArray(value)) {
    return { lectures: value as CreateLectureData[] };
  }

  if (
    value &&
    typeof value === 'object' &&
    'lectures' in value &&
    Array.isArray((value as { lectures: unknown[] }).lectures)
  ) {
    const payload = value as { deviceId?: string; lectures: CreateLectureData[] };
    return { deviceId: payload.deviceId, lectures: payload.lectures };
  }

  throw new Error('El JSON debe ser un array de lecturas o un objeto con { deviceId, lectures }');
};

const normalizeLectures = (lectures: CreateLectureData[]): CreateLectureData[] => {
  return lectures.map((lecture) => ({
    ...lecture,
    ts: String(lecture.ts),
  }));
};

export const ImportLectureFilePage: React.FC = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState<string>('');
  const [deviceId, setDeviceId] = useState('');
  const [lectures, setLectures] = useState<CreateLectureData[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [lastSuccessMessage, setLastSuccessMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const isOverLimit = lectures.length > MAX_LECTURES;
  const previewLectures = useMemo(() => lectures.slice(0, PREVIEW_LIMIT), [lectures]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setError(null);
    setLastSuccessMessage(null);
    setSelectedFileName(file.name);

    try {
      const text = await file.text();
      const parsed = parseJsonToLectures(JSON.parse(text));
      const normalized = normalizeLectures(parsed.lectures);

      if (normalized.length === 0) {
        throw new Error('El archivo no contiene lecturas');
      }

      const resolvedDeviceId = parsed.deviceId ?? normalized[0]?.skey ?? '';
      setDeviceId(resolvedDeviceId);
      setLectures(normalized);

      toast.success(`Archivo cargado: ${normalized.length} lecturas`);
    } catch (fileError) {
      const message = fileError instanceof Error ? fileError.message : 'Error leyendo archivo';
      setLectures([]);
      setDeviceId('');
      setError(message);
      setLastSuccessMessage(null);
      toast.error(message);
    }
  };

  const handleEnqueue = async () => {
    if (lectures.length === 0) {
      setError('Debes cargar un archivo con lecturas');
      return;
    }
    if (!deviceId.trim()) {
      setError('No se pudo resolver el deviceId');
      return;
    }
    if (isOverLimit) {
      setError(`Límite excedido: máximo ${MAX_LECTURES} lecturas por carga`);
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      const payload = {
        deviceId: deviceId.trim(),
        lectures: lectures.map((lecture) => ({
          skey: lecture.skey,
          ts: lecture.ts,
          tz: lecture.tz,
          coords: lecture.coords,
          type: lecture.type,
          ang: lecture.ang,
          lim: lecture.lim,
          seq: lecture.seq,
          order: lecture.order,
        })),
      };

      const response = await window.fetch(`${REDIS_API_URL}/api/v1/device-batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'No se pudo enviar el lote');
      }

      const successMessage = `Lote enviado correctamente: ${lectures.length} lecturas`;
      setLastSuccessMessage(successMessage);
      toast.success(successMessage);

      // Limpiar formulario para permitir una nueva importacion de inmediato.
      setLectures([]);
      setDeviceId('');
      setSelectedFileName('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (enqueueError) {
      const message = enqueueError instanceof Error ? enqueueError.message : 'Error desconocido';
      setError(message);
      setLastSuccessMessage(null);
      toast.error(`Error al enviar el lote: ${message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-4">
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full mx-4">
            <div className="flex items-center justify-center mb-4">
              <svg
                className="animate-spin h-8 w-8 text-brand-orange"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
              Enviando lecturas como lote
            </h3>
            <p className="text-sm text-gray-600 text-center">
              Enviando {lectures.length} lecturas al sistema de colas...
            </p>
          </div>
        </div>
      )}

      <h1 className="text-2xl font-bold mb-4 text-gray-900 flex items-center gap-2">
        <FileUp className="w-7 h-7 text-brand-orange" />
        Importar lecturas por archivo
      </h1>
      <p className="text-gray-600 mb-6">Insercion masiva a traves de archivo JSON</p>

      {lastSuccessMessage && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-green-800 flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5" />
          <span className="text-sm font-medium">{lastSuccessMessage}. Formulario limpiado.</span>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border p-4 mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Archivo JSON</label>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          onChange={handleFileChange}
          className="block w-full text-sm text-gray-700"
        />

        {selectedFileName && (
          <p className="text-sm text-gray-500 mt-2">
            Archivo: <span className="font-medium">{selectedFileName}</span>
          </p>
        )}

        <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <p className="font-medium mb-1">Tipos de evento no existentes</p>
          <p>
            Podés incluir en el JSON códigos de <code className="font-mono">type</code> de 3
            caracteres que aún no estén en el catálogo (ej. <code className="font-mono">&quot;XYZ&quot;</code>).
            El backend los auto-crea e integra las lecturas; no hace falta cargarlos antes.
          </p>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Device ID para cola</label>
          <input
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
            placeholder="Se autocompleta desde skey/deviceId del archivo"
            className="w-full border rounded px-3 py-2 text-sm"
          />
        </div>

        <div className="mt-4 flex items-center gap-2 text-sm">
          <span className="font-medium">Lecturas detectadas:</span>
          <span>{lectures.length}</span>
          {isOverLimit && (
            <span className="inline-flex items-center gap-1 text-amber-700">
              <AlertTriangle className="w-4 h-4" />
              Máximo permitido: {MAX_LECTURES}
            </span>
          )}
        </div>

        {error && <p className="text-sm text-red-600 mt-3">{error}</p>}

        <button
          onClick={handleEnqueue}
          disabled={isLoading || lectures.length === 0 || isOverLimit}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-brand-orange text-white rounded disabled:opacity-50"
        >
          <Upload className="w-4 h-4" />
          {isLoading
            ? 'Enviando lote...'
            : `Enviar ${lectures.length > 0 ? lectures.length : ''} lecturas como lote`}
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border p-4">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">Vista previa</h2>
        {lectures.length === 0 ? (
          <p className="text-sm text-gray-500">Cargá un archivo para ver la vista previa.</p>
        ) : (
          <>
            <p className="text-sm text-gray-500 mb-3">
              Mostrando {previewLectures.length} de {lectures.length} lecturas
            </p>
            <div className="overflow-auto">
              <table className="w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-brand-gray-dark">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-brand-white">Sec</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-brand-white">
                      Dispositivo
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-brand-white">
                      Fecha y hora
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-brand-white">Zona</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-brand-white">
                      Coordenadas
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-brand-white">Evento</th>
                  </tr>
                </thead>
                <tbody>
                  {previewLectures.map((lecture, index) => (
                    <tr
                      key={`${lecture.skey}-${lecture.seq}-${index}`}
                      className={`border-t ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                    >
                      <td className="px-3 py-2 text-gray-700">#{lecture.seq}</td>
                      <td className="px-3 py-2 text-gray-700">{lecture.skey}</td>
                      <td className="px-3 py-2 text-gray-700">
                        {new Date(parseInt(lecture.ts, 10) * 1000).toLocaleString('es-AR')}
                      </td>
                      <td className="px-3 py-2 text-gray-700">{lecture.tz || '-'}</td>
                      <td className="px-3 py-2 text-gray-700">
                        {lecture.coords?.lat}, {lecture.coords?.long}
                      </td>
                      <td className="px-3 py-2">
                        {(() => {
                          const styles = getEventStyles(lecture.type);
                          const IconComponent = styles.icon;
                          return (
                            <div className="inline-flex items-center gap-2">
                              <IconComponent className="h-4 w-4" style={{ color: styles.color }} />
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-semibold ${styles.bg} ${styles.text}`}
                              >
                                {lecture.type} - {t(`pages.lectures.events.${lecture.type}`, lecture.type)}
                              </span>
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
